<script setup lang="ts">
import { LucideChevronDown, LucideMenu } from 'lucide-vue-next'
import { useElementSize } from '@vueuse/core'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu'

import type { IBank } from '~/components/entity/bank'
import { getOrganizationNavLink } from '~/components/widget/bank/services-menu'

interface Props {
  organization: IBank
  type?: 'bank' | 'microcredit'
  navItems: {
    name: string
    link: string
    /** Never shown in the desktop row, only under "More" */
    inMore?: boolean
  }[]
}

const props = withDefaults(defineProps<Props>(), {
  type: 'bank'
})

const route = useRoute()
const isOpen = ref(false)
const isMobileOpen = ref(false)
const filteredNavItems = computed(() =>
  (props.navItems || []).filter((i) => {
    const key = `${i.link}_count` as keyof IBank
    if (i.link === '#about') return true
    return (
      (props.organization?.[key] && props.organization[key] !== 0) ||
      (i.link === 'rates' &&
        (props.type === 'bank' ||
          ['salym-finance', 'elet-capital'].includes(props.organization?.slug)))
    )
  })
)
const fitRoute = computed(() => {
  return route.params.slug ? route.params.slug : route.params[props.type]
})

const fitNavItems = computed(() =>
  filteredNavItems.value.map((i) => ({
    ...i,
    link: getOrganizationNavLink(props.type, fitRoute.value as string, i.link).replace(/_/g, '-')
  }))
)

const isActive = (link: string) => route.path.replace(/^\/ky/, '') === link
const activeItem = computed(() => fitNavItems.value.find((i) => isActive(i.link)))

// From `sm` the menu is a single row. Items that do not fit wrap to a second line, which is clipped
// by CSS, so the visible items are right already in the SSR markup. The script only finds the
// first wrapped item to fill "More".
const rowItems = computed(() => fitNavItems.value.filter((i) => !i.inMore))
const rowRef = ref<HTMLElement | null>(null)
const { width: rowWidth } = useElementSize(rowRef)
const { locale } = useI18n()
const hiddenFrom = ref<number | null>(null)

function measure() {
  const items = Array.from(rowRef.value?.children ?? []) as HTMLElement[]
  const firstTop = items[0]?.offsetTop
  const index = items.findIndex((item) => item.offsetTop > (firstTop ?? 0))
  hiddenFrom.value = index === -1 ? null : index
}

watch([rowWidth, rowItems, locale], () => nextTick(measure))
// Item widths change once web fonts are loaded
onMounted(() => document.fonts?.ready.then(measure))

const dropdownItems = computed(() => [
  ...(hiddenFrom.value === null ? [] : rowItems.value.slice(hiddenFrom.value)),
  ...fitNavItems.value.filter((i) => i.inMore)
])
</script>

<template>
  <nav class="relative">
    <!-- Mobile: all sections in a burger dropdown, labelled with the current section -->
    <DropdownMenu v-model:open="isMobileOpen">
      <DropdownMenuTrigger as-child>
        <Button variant="outline-gray" class="w-full justify-between sm:hidden">
          <span class="flex min-w-0 items-center gap-2">
            <LucideMenu class="size-4" aria-hidden="true" />
            <span class="truncate">{{ $t(activeItem?.name ?? 'common.sections') }}</span>
          </span>
          <LucideChevronDown
            class="size-4 transition-transform duration-200"
            :class="{ 'rotate-180': isMobileOpen }"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-[--reka-dropdown-menu-trigger-width]">
        <DropdownMenuItem v-for="item in fitNavItems" :key="item.name">
          <NuxtLinkLocale
            :to="item.link"
            :class="[{ 'text-blue-600': isActive(item.link) }, 'block w-full font-semibold']"
          >
            {{ $t(item.name) }}
          </NuxtLinkLocale>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <div class="hidden items-start gap-6 sm:flex">
      <ul ref="rowRef" class="flex h-5 min-w-0 flex-wrap gap-x-6 overflow-hidden leading-5">
        <li
          v-for="(item, index) in rowItems"
          :key="item.name"
          :class="{ invisible: hiddenFrom !== null && index >= hiddenFrom }"
        >
          <NuxtLinkLocale
            :to="item.link"
            :class="[
              { underline: isActive(item.link) },
              'whitespace-nowrap font-semibold leading-5 text-blue-600 hover:underline'
            ]"
          >
            {{ $t(item.name) }}
          </NuxtLinkLocale>
        </li>
      </ul>

      <!-- Space for "More" is always reserved, so showing it does not move the items -->
      <DropdownMenu v-model:open="isOpen">
        <DropdownMenuTrigger
          class="flex shrink-0 items-center whitespace-nowrap font-semibold leading-5 text-blue-600 hover:underline"
          :class="{ invisible: !dropdownItems.length }"
          :tabindex="dropdownItems.length ? undefined : -1"
          type="button"
        >
          {{ $t('widget.bank_services_menu.more') }}
          <LucideChevronDown
            class="ml-1 h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': isOpen }"
            aria-hidden="true"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem v-for="item in dropdownItems" :key="item.name">
            <NuxtLinkLocale
              :to="item.link"
              :class="[{ 'text-blue-600': isActive(item.link) }, 'block w-full']"
            >
              {{ $t(item.name) }}
            </NuxtLinkLocale>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </nav>
</template>
